import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createSampleRequest } from "@/lib/api";
import { trackSampleRequested } from "@/lib/analytics";

const ROLES = ["Architect", "Interior Designer", "Builder", "Specifier"];
const MATERIALS = ["Timber", "Carpet", "Tile", "Stone"];

export default function SampleForm({ onDone }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", address: "", notes: "" });
  const [role, setRole] = useState("Interior Designer");
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (m) => setMaterials((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createSampleRequest({ ...form, role, materials });
      trackSampleRequested({ role, materials });
      toast.success("Sample request received", {
        description: "Our studio team will be in touch to curate and organise your samples.",
      });
      setForm({ name: "", email: "", company: "", address: "", notes: "" });
      setMaterials([]);
      setTimeout(() => onDone?.(), 700);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="sample-form" className="space-y-7">
      <div className="grid sm:grid-cols-2 gap-6">
        <input data-testid="sample-name" className="field-underline" placeholder="Full name" value={form.name} onChange={set("name")} />
        <input data-testid="sample-email" type="email" className="field-underline" placeholder="Trade email address" value={form.email} onChange={set("email")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <input data-testid="sample-company" className="field-underline" placeholder="Studio / Practice" value={form.company} onChange={set("company")} />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="sample-role" className="field-underline rounded-none border-0 border-b border-[#D6D1CA]/25 bg-transparent text-[#D6D1CA] focus:ring-0 h-auto px-0.5 py-2.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#1b1c1f] border-[#8C7355]/25 text-[#F5F4F0]">
            {ROLES.map((r) => (
              <SelectItem key={r} value={r} className="focus:bg-[#8C7355]/20 focus:text-[#F5F4F0]">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <p className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase mb-3">Select elements</p>
        <div className="flex flex-wrap gap-3">
          {MATERIALS.map((m) => {
            const on = materials.includes(m);
            return (
              <button
                key={m}
                type="button"
                data-testid={`sample-material-${m.toLowerCase()}`}
                onClick={() => toggle(m)}
                className="inline-flex items-center gap-2 rounded-full border px-5 py-2 text-xs tracking-wide"
                style={{
                  borderColor: on ? "#8C7355" : "rgba(214,209,202,0.25)",
                  background: on ? "rgba(140,115,85,0.18)" : "transparent",
                  color: on ? "#F5F4F0" : "rgba(214,209,202,0.7)",
                  transition: "all 0.35s ease",
                }}
              >
                {on && <Check size={13} className="text-[#C9A876]" />}
                {m}
              </button>
            );
          })}
        </div>
      </div>

      <input data-testid="sample-address" className="field-underline" placeholder="Delivery address" value={form.address} onChange={set("address")} />
      <textarea data-testid="sample-notes" rows={3} className="field-underline resize-none" placeholder="Project brief — timing, scope, specific finishes of interest (optional)" value={form.notes} onChange={set("notes")} />

      <button type="submit" disabled={loading} data-testid="sample-submit" className="btn-bronze w-full sm:w-auto rounded-full px-10 py-3.5 text-[11px] tracking-[0.2em] uppercase font-medium disabled:opacity-60">
        {loading ? "Sending…" : "Request Sample"}
      </button>
    </form>
  );
}
